import React, { useState, useEffect } from "react";
import { Upload, PlusCircle, Trash } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ProductMockup from "./LaptopSkinMokup";
import axios from "axios";
import toast from "react-hot-toast";

const CustomProductForm = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [productType, setProductType] = useState("laptop skin");
  const [sizes, setSizes] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          "https://api.elza2.store/api/v1/categories/custom"
        );
        const data = await response.json();
        setCategories(data.categories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to load categories");
      }
    };
    fetchCategories();
  }, []);

  const handleImageUpload = (e) => {
    const newImages = Array.from(e.target.files);

    if (images.length + newImages.length > 20) {
      toast.error("Maximum 20 images allowed");
      return;
    }

    const validImages = newImages.filter((image) => {
      if (image.size > 10 * 1024 * 1024) {
        toast.error(`${image.name} exceeds 10MB size limit`);
        return false;
      }
      return true;
    });

    setImages((prevImages) => [...prevImages, ...validImages]);
  };

  const handleRemoveImage = (index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleCategoryChange = (id, name, sizes) => {
    setSelectedCategory(id);
    setProductType(name);
    setSizes(sizes || []);
    setSelectedSize(null); // Reset size when category changes
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!selectedCategory) {
        toast.error("Please select a category");
        setIsLoading(false);
        return;
      }

      if (sizes.length > 0 && !selectedSize) {
        toast.error("Please select a size");
        setIsLoading(false);
        return;
      }

      if (images.length === 0) {
        toast.error("Please upload at least one image");
        setIsLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("quantity", quantity);
      formData.append("description", description);
      formData.append("category", selectedCategory);

      // Only append size if it exists
      if (selectedSize) {
        formData.append("size", selectedSize.size);
        formData.append("price", selectedSize.price);
      }

      images.forEach((image) => formData.append("images", image));

      const response = await axios.post(
        "https://api.elza2.store/api/v1/custom",
        formData,
        {
          headers: {
            Authorization: localStorage.getItem("Auth"),
          },
        }
      );

      if (response.status === 201) {
        toast.success(
          "Custom product created and added to cart successfully! 🛒"
        );
        // Reset form fields
        setQuantity(1);
        setDescription("");
        setImages([]);
        setSelectedCategory("");
        setSelectedSize(null);
        setSizes([]);
        setIsLoading(false);
        setTimeout(() => {
          navigate("/cart");
        }, 1000);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        error.response?.data?.message || "Failed to create custom product"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid md:grid-cols-2 gap-8 items-start">
        <div className="bg-gray-50 rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            Product Preview
          </h2>
          <div className="grid grid-cols-1 gap-4">
            {images.length > 0 ? (
              images.map((image, index) => (
                <div
                  key={index}
                  className="relative group rounded-lg overflow-hidden"
                >
                  <ProductMockup
                    images={[URL.createObjectURL(image)]}
                    onRemoveImage={handleRemoveImage}
                  />
                  <button
                    onClick={() => handleRemoveImage(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                </div>
              ))
            ) : (
              <div className="col-span-3 flex flex-col items-center justify-center text-gray-400 p-8">
                <p>No images uploaded</p>
              </div>
            )}
          </div>
          <div className="mt-4">
            <label
              htmlFor="images"
              className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 cursor-pointer"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload Images
            </label>
            <input
              type="file"
              id="images"
              multiple
              onChange={handleImageUpload}
              className="hidden"
              accept="image/*"
              required
            />
          </div>
          <div className="mt-4 space-y-2">
            <div className="text-sm text-gray-600 explore-btn p-4 rounded-lg border border-blue-200">
              <h3 className="font-medium text-black mb-2">
                Image Requirements:
              </h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>
                  Maximum 20 images allowed per request. You can make a new
                  request after.
                </li>
                <li>Each image must be under 10MB</li>
                {productType.toLowerCase().includes("sticker") && (
                  <li className="text-amber-800 font-medium">
                    For stickers: Please ensure each image contains only one
                    sticker design
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-6 bg-white rounded-xl p-6 shadow-sm"
        >
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="category"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Category
              </label>
              <select
                id="category"
                value={selectedCategory}
                onChange={(e) => {
                  const selected = categories.find(
                    (category) => category._id === e.target.value
                  );
                  handleCategoryChange(
                    selected ? selected._id : "",
                    selected ? selected.name : "",
                    selected ? selected.sizes : []
                  );
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            {sizes.length > 0 && (
              <div>
                <label
                  htmlFor="size"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Size
                </label>
                <select
                  id="size"
                  value={selectedSize ? JSON.stringify(selectedSize) : ""}
                  onChange={(e) => {
                    const sizeData = e.target.value
                      ? JSON.parse(e.target.value)
                      : null;
                    setSelectedSize(sizeData);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select Size</option>
                  {sizes.map((size, index) => (
                    <option key={index} value={JSON.stringify(size)}>
                      Size: {size.size}, Price: {size.price}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {productType.toLowerCase() === "laptop skin"
                ? "Description and Laptop Model"
                : "Description"}
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter product description"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required={productType.toLowerCase() === "laptop skin"}
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-2 px-4 bg-indigo-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                isLoading
                  ? "opacity-75 cursor-not-allowed"
                  : "hover:bg-indigo-700"
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomProductForm;
